import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import artifactViewersCopy from '@/i18n/ns/artifact-viewers';
import { WorkspaceIcon } from '../icons';
import type { DeckPayload } from '../types';
import { DeckStage } from './stage';

// Present: one slide on a dark stage, started from the slide the person is on.
// Notes belong to the presenter, so they sit in a panel the audience view of
// the slide never carries, and they stay open from slide to slide once asked
// for. Motion between slides is DES2's (ui/lib/motion); this file moves nothing.

const SWIPE_PX = 48;
const FOCUSABLE = 'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';

// A pointer gesture is a swipe only when it is mostly horizontal and long
// enough; anything else is a tap or a scroll and steps nothing.
export function swipeStep(dx: number, dy: number): -1 | 0 | 1 {
  if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}

export function Presenter({
  title,
  slide,
  at,
  count,
  onStep,
  onClose,
}: {
  title?: string;
  slide: DeckPayload['slides'][number];
  at: number;
  count: number;
  onStep: (by: number) => void;
  onClose: () => void;
}) {
  const copy = useNamespaceCopy(artifactViewersCopy);
  const [notes, setNotes] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const toggleNotes = useCallback(() => setNotes((was) => !was), []);

  useEffect(() => {
    const keys: Record<string, () => void> = {
      ArrowRight: () => onStep(1),
      ArrowDown: () => onStep(1),
      PageDown: () => onStep(1),
      ' ': () => onStep(1),
      ArrowLeft: () => onStep(-1),
      ArrowUp: () => onStep(-1),
      PageUp: () => onStep(-1),
      // The filmstrip yields every key while the presenter is up, so the jumps
      // it offers have to exist here too. onStep clamps, so overshooting the
      // ends is how you ask.
      Home: () => onStep(-count),
      End: () => onStep(count),
      n: toggleNotes,
      N: toggleNotes,
      Escape: onClose,
    };
    const onKey = (event: KeyboardEvent) => {
      // A control keeps its own Space/Enter; only the stage steps on Space.
      if (event.key === ' ' && event.target instanceof HTMLButtonElement) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Tab') {
        // Focus stays inside the presenter while it is a modal dialog. Focus
        // can sit on something that is not in the list: the dialog root itself
        // (where it starts), a control that has just been disabled, or nothing
        // inside at all. From any of those the next stop is an end of the list,
        // never wherever the browser would go next.
        const nodes = [...(root.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
        event.preventDefault();
        if (nodes.length === 0) return root.current?.focus();
        const at = nodes.indexOf(document.activeElement as HTMLElement);
        const next =
          at === -1 ? (event.shiftKey ? nodes.length - 1 : 0) : at + (event.shiftKey ? -1 : 1);
        nodes[(next + nodes.length) % nodes.length].focus();
        return;
      }
      // hasOwn, not a bare lookup: `keys.constructor` is a function on every
      // object literal, and a key named for one would otherwise be "handled".
      const act = Object.hasOwn(keys, event.key) ? keys[event.key] : undefined;
      if (!act) return;
      event.preventDefault();
      act();
    };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll while a slide is filling the screen.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus moves into the dialog and returns to what opened it.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    root.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [onStep, onClose, count, toggleNotes]);

  const label = formatCopy(copy.slideOf, { at: at + 1, count });
  // A portal on body, so the presenter is not clipped by the canvas's own
  // scroll container or its stacking context.
  return createPortal(
    <div
      ref={root}
      className="ws-present"
      role="dialog"
      aria-modal="true"
      aria-label={copy.presenting}
      tabIndex={-1}
      data-notes={notes ? 'open' : 'closed'}
    >
      <header className="ws-present-top">
        <span className="ws-present-title">{title}</span>
        <span className="ws-present-hint">{copy.moveHint}</span>
        <button className="ws-present-exit" onClick={onClose} aria-label={copy.stopPresenting}>
          <WorkspaceIcon name="close" size={16} />
          <span>{copy.stopPresenting}</span>
        </button>
      </header>
      <div
        className="ws-present-stage"
        onPointerDown={(event) => {
          start.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => {
          const from = start.current;
          start.current = null;
          if (!from) return;
          const by = swipeStep(event.clientX - from.x, event.clientY - from.y);
          if (by) onStep(by);
        }}
        onPointerCancel={() => {
          start.current = null;
        }}
      >
        <DeckStage slide={slide} label={label} />
      </div>
      <p className="ws-present-live" role="status" aria-live="polite">
        {label}
      </p>
      {notes && (
        <section className="ws-present-notes" aria-label={copy.notesPanel}>
          <h2>{copy.notesLabel}</h2>
          {slide.notes ? <p>{slide.notes}</p> : <p className="empty">{copy.noNotes}</p>}
        </section>
      )}
      <div className="ws-present-step">
        <button
          className="ws-present-notes-toggle"
          aria-pressed={notes}
          onClick={toggleNotes}
          title={copy.presentHint}
        >
          {notes ? copy.hideNotes : copy.showNotes}
        </button>
        <button onClick={() => onStep(-1)} disabled={at === 0} aria-label={copy.previousSlide}>
          <WorkspaceIcon name="chevron" size={14} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <span className="ws-present-count">
          {at + 1} / {count}
        </span>
        <button onClick={() => onStep(1)} disabled={at === count - 1} aria-label={copy.nextSlide}>
          <WorkspaceIcon name="chevron" size={14} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
